import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { AddUserGroupMembershipDialog } from "#src/batteries-included-components/Dialogs/AddUserGroupMembershipDialog";
import { DeleteUserGroupMembershipDialog } from "#src/batteries-included-components/Dialogs/DeleteUserGroupMembershipDialog";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  useClearUserGroupCache,
  useListUserGroups,
} from "#src/components/hooks/adapters/useUserGroups";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { UserDetailContext } from "#src/routes/settings/users/detail/UserDetailContext";
import { UserGroupDetailsRoutePath } from "#src/routes/settings/users/groups/[groupId]/details";
import { useMutation, useQueries } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  HeaderType,
  Icon,
  ItemAction,
  useToast,
} from "@validereinc/common-components";
import {
  DomainAdapterError,
  SortDirection,
  UserGroupMembershipSchema,
  UserGroupsAdapter,
  UserGroupSchema,
  UserGroupType,
  type UserGroupMembershipType,
  type UserReturnType,
} from "@validereinc/domain";
import pLimit from "p-limit";
import React, { useContext, useState } from "react";

export const UserGroupMembershipTablePanel = () => {
  const { toast } = useToast();
  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["user-group-memberships", "list"]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    undefined,
    tableStateStorageKey
  );
  const [isAddUserUserGroupOpen, setIsAddUserUserGroupOpen] =
    useState<boolean>();
  const [groupToDeleteFrom, setItemToRemove] = useState<UserGroupType | null>();
  const [isUserGroupWriteEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:roles_permissions",
    permissionQuery: "user_groups:write",
  });
  const [isUserGroupDeleteEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:roles_permissions",
    permissionQuery: "user_groups:delete",
  });
  const { userDetails } = useContext(UserDetailContext) ?? {};
  const [selected, setSelected] = useState<Record<string, UserGroupType>>({});

  const userId = userDetails?.data?.id;
  const isUserIdDefined = !!userId;

  const params = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: { "user.id": [userId] },
  };

  const { data, isLoading } = useListUserGroups(params, {
    enabled: isUserIdDefined,
  });
  const membershipsQueries = useQueries({
    queries:
      data?.data.map((userGroup) => ({
        queryKey: ["users", "groups", userGroup.id, "memberships", userId],
        queryFn: () =>
          UserGroupsAdapter.members
            .getList({ meta: { userGroupId: userGroup.id } })
            .then((resp) =>
              resp.data.filter((member) => member.user_id === userId)
            ),
        enabled: !!userGroup.id,
      })) ?? [],
  });
  const membershipsMap = membershipsQueries.reduce(
    (acc, query) => {
      if (query.data?.[0]?.user_group_id)
        acc[query.data[0].user_group_id] = query.data;
      return acc;
    },
    {} as Record<string, UserGroupMembershipType[]>
  );
  const { invalidateUserGroup } = useClearUserGroupCache();
  const removeFromUserGroupsMutation = useMutation({
    mutationFn: ({
      user,
      groupIds,
    }: {
      user: UserReturnType;
      groupIds: string[];
    }) => {
      const limitPromises = pLimit(8);

      return Promise.all(
        groupIds.map((groupId) =>
          limitPromises(() =>
            UserGroupsAdapter.members.remove({
              id: user.id,
              meta: { userGroupId: groupId },
            })
          )
        )
      );
    },
    onSuccess: (_, variables) => {
      invalidateUserGroup();
      setSelected({});
      toast.push({
        intent: "success",
        description: `Successfully removed ${userDetails?.data?.name} from ${variables.groupIds.length} user group(s)`,
      });
    },
    onError: (err) => {
      const normalizedErr = new DomainAdapterError(
        `Failed to remove ${userDetails?.data?.name} from user group(s)`,
        {
          cause: err,
          getExplanation: () => "Failed to remove user from user group(s)",
        }
      );
      toast.push({
        intent: "error",
        description: normalizedErr.getExplanation(),
      });
    },
  });

  const headers: Array<HeaderType<UserGroupType>> = [
    {
      key: UserGroupSchema.keyof().Enum.name,
      label: "Name",
      isSortable: true,
      renderComponent: ({ item }) => {
        return (
          <RoutingLink
            to={UserGroupDetailsRoutePath.toLink({
              pathParams: { groupId: item.id },
            })}
          >
            {item.name}
          </RoutingLink>
        );
      },
    },
    {
      key: UserGroupSchema.keyof().Enum.description,
      label: "Description",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell value={item.description} />
      ),
    },
    {
      key: UserGroupMembershipSchema.keyof().Enum.created_at,
      label: "Member Since",
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          isLoading={membershipsQueries.some((q) => q.isLoading)}
          isBusy={membershipsQueries.some((q) => q.isFetching)}
          value={membershipsMap[item.id]?.[0]?.created_at}
          withTime
        />
      ),
    },
  ];

  const getItemActions = ({ item }: { item: UserGroupType }): ItemAction[] => [
    {
      label: "Remove from Group",
      buttonProps: {
        onClick: () => setItemToRemove(item),
        icon: "user-minus",
      },
    },
  ];

  const actionRow = [
    <Button
      key="add-user-user-group"
      onClick={() => setIsAddUserUserGroupOpen(true)}
      slotLeft={<Icon variant="user-plus" />}
    >
      Add to Group
    </Button>,
  ];

  const items = data?.data ?? [];

  return (
    <>
      <DataTablePanel
        panelProps={{ title: "User Groups", actionRow, isFluidY: false }}
        dataTableProps={{
          items,
          isLoading,
          headers,
          getItemActions,
          getItemBulkActions: () => [
            <Button
              key="remove-from-group"
              variant="error-outline"
              icon="user-minus"
              disabled={!userDetails?.data}
              isLoading={removeFromUserGroupsMutation.isLoading}
              onClick={() => {
                if (!userDetails?.data) return;

                removeFromUserGroupsMutation.mutate({
                  user: userDetails?.data,
                  groupIds: Object.keys(selected),
                });
              }}
            >
              Remove from Groups
            </Button>,
          ],
          getItemId: (item) => item.id,
          selected,
          onSelectionChange: setSelected,
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: data?.total_entries,
          },
        }}
      />
      {isUserGroupWriteEnabled && userDetails?.data ? (
        <AddUserGroupMembershipDialog
          isOpen={!!isAddUserUserGroupOpen}
          onClose={() => setIsAddUserUserGroupOpen(false)}
          user={userDetails.data}
        />
      ) : null}
      {isUserGroupDeleteEnabled && userDetails?.data && groupToDeleteFrom ? (
        <DeleteUserGroupMembershipDialog
          isOpen={!!groupToDeleteFrom}
          onClose={() => setItemToRemove(null)}
          groupToDeleteFrom={groupToDeleteFrom}
          user={userDetails.data}
        />
      ) : null}
    </>
  );
};
